import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useQuery } from 'react-query';
import styled from 'styled-components';

import { Modal } from 'app/theme/Modal';
import { DropzoneAlert } from 'pkg/components/FileUploader/DropzoneAlert';
import { FileUploaderButton } from 'pkg/components/FileUploader/FileUploaderButton';
import { FileUploaderErrorFallback } from 'pkg/components/FileUploader/FileUploaderErrorFallback';
import { fileUploaderService, getExtensionsByFileNames } from 'pkg/components/FileUploader/fileUploaderService';
import { useFileUploader } from 'pkg/components/FileUploader/useFileUploader';
import { AlertState, useFileUploaderAlerts } from 'pkg/components/FileUploader/useFileUploaderAlerts';

import { FileUploaderDropzoneContent } from './FileUploaderContent/FileUploaderDropzone';

export interface FileUploaderProps {
  trigger?: JSX.Element;
  onChange: (files: string[]) => void;
  onFilesRejection?: (files: string[]) => void;
  value: string[];
}

const StyledModal = styled(Modal)`
  &&& {
    border-radius: 2rem;
    height: 100%;
    width: 100%;
    max-width: 48rem;
    max-height: 50rem;
  }
`;

const MAX_FILES_SIZE = 10000000000;

const FileUploaderDropzone = forwardRef<{ openModal: () => void }, FileUploaderProps>(
  ({ onChange, onFilesRejection, value }, ref) => {
    const { data: allowedTypes } = useQuery<string[]>(
      fileUploaderService.queryKey,
      fileUploaderService.getAllowedFileTypes
    );
    const { files, removeFile, addFiles, revertFiles, clearFiles, submitFiles, loading } = useFileUploader({
      onChange,
      value,
    });

    const { uploaderState, actions } = useFileUploaderAlerts({
      values: value,
      files,
      revertFiles,
      loading,
      submitFiles,
    });

    const {
      disableAlert,
      handleCloseWithPendingFiles,
      handleSubmit,
      openModal,
      setRejectedFiles,
      openTooBigFilesAlert,
    } = actions;
    const { alert, fileTypes } = uploaderState;

    useImperativeHandle<{ openModal: () => void }, { openModal: () => void }>(ref, () => ({ openModal }), [openModal]);

    const handleAddFiles = async (newFiles: File[]) => {
      const totalSize =
        files.reduce((size, { file }) => size + (file.size ?? 0), 0) +
        newFiles.reduce((size, file) => size + (file.size ?? 0), 0);
      if (totalSize <= MAX_FILES_SIZE) {
        await addFiles(newFiles);
      } else {
        openTooBigFilesAlert();
      }
    };

    useEffect(() => {
      submitFiles();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    return (
      <FileUploaderDropzoneContent
        clearFiles={clearFiles}
        onSubmit={handleSubmit}
        loading={loading}
        onFileRejection={async (newFiles) => {
          const extensions = getExtensionsByFileNames(newFiles);
          onFilesRejection?.(extensions);
          await setRejectedFiles(extensions);
        }}
        disabled={alert !== AlertState.DISABLED}
        files={files}
        removeFile={removeFile}
        addFiles={handleAddFiles}
        accept={allowedTypes}
      >
        <DropzoneAlert
          alert={alert}
          disableAlert={disableAlert}
          closeWithPendingFiles={handleCloseWithPendingFiles}
          fileTypes={fileTypes}
        />
      </FileUploaderDropzoneContent>
    );
  }
);

export const FileUploaderArea = forwardRef<{ openModal: () => void }, FileUploaderProps>((props, ref) => (
  <ErrorBoundary
    fallbackRender={(fallbackProps) => (
      <StyledModal open trigger={props.trigger ?? <FileUploaderButton disabled />}>
        <FileUploaderErrorFallback {...fallbackProps} />
      </StyledModal>
    )}
  >
    <FileUploaderDropzone ref={ref} {...props} />
  </ErrorBoundary>
));
